"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Alert,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from "@mui/material"
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
} from "@mui/icons-material"
import { useAuth } from "../../contexts/AuthContext"
import useOrganizationParams from "../../hooks/useOrganizationParams"
import webhookService from "../../services/webhook.service"
import { buildSamplePayload } from "../../utils/webhook-samples"
import PageHeader from "../../components/common/PageHeader"


// Steps for the webhook creation process
const steps = ["Basic Information", "Select Events", "Test & Verify", "Review & Create"]

const CreateWebhook = () => {
  const navigate = useNavigate()
  const { selectedOrganization: organization, user } = useAuth()
  const organizationParams = useOrganizationParams()
  const [orgError, setOrgError] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [testLoading, setTestLoading] = useState(false)
  const [testSuccess, setTestSuccess] = useState(false)
  const [testResponse, setTestResponse] = useState(null)

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    url: "",
    events: [],
    isActive: true,
  })

  // Validation state
  const [errors, setErrors] = useState({
    name: "",
    url: "",
    events: "",
  })

  // Test payload state
  const [testPayload, setTestPayload] = useState("")
  const [selectedTestEvent, setSelectedTestEvent] = useState("")
  const [webhookEvents, setWebhookEvents] = useState([])

  // Notification state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  })

  useEffect(() => {
    if (formData.events.length > 0 && !formData.events.includes(selectedTestEvent)) {
      setSelectedTestEvent(formData.events[0])
    }
  }, [formData.events])

  useEffect(() => {
    if (selectedTestEvent) {
      setTestPayload(JSON.stringify(buildSamplePayload(selectedTestEvent), null, 2))
    }
  }, [selectedTestEvent])

  useEffect(() => {
    webhookService.getWebhookEvents().then((res) => setWebhookEvents(res.data.data || []))
  }, [])

  // Check if organization data is available
  useEffect(() => {
    if (!organization && user) {
      // Only show organization error for superadmins
      if (user.role === "superadmin") {
        setOrgError(true)
        showSnackbar("Organization data not available. Please select an organization first.", "error")
      } else {
        // Regular users don't need to select an organization
        setOrgError(false)
      }
    } else if (organization) {
      setOrgError(false)
    }
  }, [organization, user])

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })

    // Clear error when field is updated
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" })
    }
  }

  // Handle event selection changes
  const handleEventsChange = (e) => {
    setFormData({ ...formData, events: e.target.value })

    // Clear error when field is updated
    if (errors.events) {
      setErrors({ ...errors, events: "" })
    }
  }

  // Handle active toggle
  const handleActiveChange = (event) => {
    setFormData({ ...formData, isActive: event.target.checked })
  }

  // Show notification
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity })
  }

  // Close notification
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  // Validate form data for the current step
  const validateCurrentStep = () => {
    const newErrors = { ...errors }
    let isValid = true

    if (activeStep === 0) {
      // Validate name and URL
      if (!formData.name.trim()) {
        newErrors.name = "Webhook name is required"
        isValid = false
      }

      if (!formData.url.trim()) {
        newErrors.url = "Endpoint URL is required"
        isValid = false
      } else if (!/^https?:\/\/.+/.test(formData.url)) {
        newErrors.url = "URL must start with http:// or https://"
        isValid = false
      }
    } else if (activeStep === 1) {
      // Validate events
      if (formData.events.length === 0) {
        newErrors.events = "At least one event must be selected"
        isValid = false
      }
    }

    setErrors(newErrors)
    return isValid
  }

  // Handle next step
  const handleNext = () => {
    if (validateCurrentStep()) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }
  }

  // Handle back step
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  // Handle test webhook
  const handleTestWebhook = async () => {
    try {
      setTestLoading(true)
      setTestSuccess(false)
      setTestResponse(null)

      let payload
      try {
        payload = JSON.parse(testPayload)
      } catch (e) {
        showSnackbar("Invalid JSON payload", "error")
        setTestLoading(false)
        return
      }

      // Create a temporary webhook for testing
      const tempWebhook = {
        ...formData,
        organizationId: organization?._id || user?.organizationId,
        isTemporary: true,
      }

      const response = await webhookService.testWebhookEndpoint(tempWebhook, payload)
      setTestResponse(response.data)
      setTestSuccess(true)
      showSnackbar("Webhook test successful!", "success")
    } catch (error) {
      setTestResponse(error.response?.data || { error: "Failed to connect to endpoint" })
      showSnackbar("Webhook test failed", "error")
    } finally {
      setTestLoading(false)
    }
  }

  // Handle create webhook
  const handleCreateWebhook = async () => {
    try {
      // For superadmins, check if organization exists
      if (user?.role === "superadmin" && !organization) {
        showSnackbar("Organization data not available. Please select an organization first.", "error")
        setOrgError(true)
        return
      }

      setLoading(true)

      const webhookData = {
        ...formData,
        // For regular users, use their default organization ID from the user object
        organizationId: organization?._id || user?.organizationId,
      }

      await webhookService.createWebhook(webhookData, organizationParams)
      showSnackbar("Webhook created successfully")

      // Navigate back to webhook manager after a short delay
      setTimeout(() => {
        navigate("/webhooks")
      }, 1500)
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Failed to create webhook", "error")
      console.error("Error creating webhook:", error)
      setLoading(false)
    }
  }

  // Handle copy to clipboard
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text)
    showSnackbar("Copied to clipboard")
  }

  // Render step content
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="Webhook Name"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                required
                error={!!errors.name}
                helperText={errors.name || "A descriptive name to identify this webhook"}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="url"
                label="Endpoint URL"
                value={formData.url}
                onChange={handleInputChange}
                fullWidth
                required
                error={!!errors.url}
                helperText={
                  errors.url || "The URL that will receive webhook events (must start with http:// or https://)"
                }
                placeholder="https://example.com/webhook"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={formData.isActive} onChange={handleActiveChange} color="primary" />}
                label="Active"
              />
              <FormHelperText>
                Enable or disable this webhook. Inactive webhooks won't receive any events.
              </FormHelperText>
            </Grid>
          </Grid>
        )
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth required error={!!errors.events}>
                <InputLabel>Events</InputLabel>
                <Select
                  multiple
                  name="events"
                  value={formData.events}
                  onChange={handleEventsChange}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {webhookEvents.map((event) => (
                    <MenuItem key={event.id} value={event.id}>
                      {event.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.events && <FormHelperText>{errors.events}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Event Categories
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Subscription Events
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Events related to subscription lifecycle, including creation, updates, cancellations, and
                        payment status.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Customer Events
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Events related to customer management, including creation and updates to customer information.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Invoice Events
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Events related to invoices, including creation, payment success, and payment failures.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )
      case 2:
        return (
          <Grid container spacing={3}>
            {formData.events.length > 1 && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Test Event</InputLabel>
                  <Select
                    value={selectedTestEvent}
                    label="Test Event"
                    onChange={(e) => setSelectedTestEvent(e.target.value)}
                  >
                    {formData.events.map((event) => (
                      <MenuItem key={event} value={event}>{event}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Test Payload
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Edit the payload below to test your webhook endpoint. This will send a POST request to your endpoint.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={10}
                value={testPayload}
                onChange={(e) => setTestPayload(e.target.value)}
                sx={{ fontFamily: "monospace" }}
                InputProps={{
                  sx: { fontFamily: "monospace" },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={handleTestWebhook}
                disabled={testLoading}
              >
                {testLoading ? <CircularProgress size={24} /> : "Test Webhook"}
              </Button>
            </Grid>
            {testResponse && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, mt: 2, bgcolor: testSuccess ? "#f0f7f0" : "#fff8f8" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography variant="subtitle1" color={testSuccess ? "success.main" : "error.main"}>
                      {testSuccess ? "Test Successful" : "Test Failed"}
                    </Typography>
                    <Chip
                      label={testSuccess ? "Success" : "Failed"}
                      color={testSuccess ? "success" : "error"}
                      size="small"
                    />
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Response:
                  </Typography>
                  <pre
                    style={{
                      margin: 0,
                      padding: "8px",
                      backgroundColor: "#f5f5f5",
                      borderRadius: "4px",
                      overflowX: "auto",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {JSON.stringify(testResponse, null, 2)}
                  </pre>
                </Paper>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Webhook Security Best Practices
              </Typography>
              <Typography variant="body2" paragraph>
                To ensure your webhook endpoint is secure:
              </Typography>
              <ul>
                <li>
                  <Typography variant="body2">
                    Verify the webhook signature using the secret provided when the webhook is created
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">Always use HTTPS for your endpoint URL</Typography>
                </li>
                <li>
                  <Typography variant="body2">Implement proper error handling and retry logic</Typography>
                </li>
                <li>
                  <Typography variant="body2">Respond with a 2xx status code to acknowledge receipt</Typography>
                </li>
              </ul>
            </Grid>
          </Grid>
        )
      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Webhook Configuration Summary
              </Typography>
              <Paper sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="subtitle2">Name:</Typography>
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <Typography variant="body1">{formData.name}</Typography>
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <Typography variant="subtitle2">URL:</Typography>
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <Typography variant="body1">{formData.url}</Typography>
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <Typography variant="subtitle2">Status:</Typography>
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <Chip
                      label={formData.isActive ? "Active" : "Inactive"}
                      color={formData.isActive ? "success" : "default"}
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <Typography variant="subtitle2">Events:</Typography>
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {formData.events.map((event) => (
                        <Chip key={event} label={event} size="small" />
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Webhook Secret
              </Typography>
              <Typography variant="body2" paragraph>
                A webhook secret will be generated when you create this webhook. You'll need this secret to verify the
                authenticity of webhook payloads.
              </Typography>
              <Paper sx={{ p: 3, bgcolor: "#f5f5f5" }}>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  The secret will be displayed only once after creation.
                </Typography>
                <Box sx={{ mt: 2, display: "flex", alignItems: "center" }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CopyIcon />}
                    onClick={() => handleCopy("Example code for verifying webhook signatures")}
                  >
                    Copy Verification Example
                  </Button>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Next Steps
              </Typography>
              <Typography variant="body2" paragraph>
                After creating this webhook:
              </Typography>
              <ol>
                <li>
                  <Typography variant="body2" paragraph>
                    Copy the webhook secret and store it securely
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" paragraph>
                    Implement signature verification in your endpoint
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" paragraph>
                    Test the webhook with real events
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" paragraph>
                    Monitor webhook deliveries in the webhook manager
                  </Typography>
                </li>
              </ol>
            </Grid>
          </Grid>
        )
      default:
        return "Unknown step"
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Create Webhook"
        description="Set up a new webhook to receive real-time event notifications"
        actions={
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate("/webhooks")}>
            Back to Webhooks
          </Button>
        }
      />
      {user?.role === "superadmin" && orgError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Organization data is not available. Please select an organization from the organization selector in the header
          before creating a webhook.
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ mt: 2, mb: 4 }}>{getStepContent(activeStep)}</Box>

        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Button disabled={activeStep === 0} onClick={handleBack}>
            Back
          </Button>
          <Box>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateWebhook}
                disabled={loading || (user?.role === "superadmin" && !organization)}
                startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
              >
                Create Webhook
              </Button>
            ) : (
              <Button variant="contained" color="primary" onClick={handleNext}>
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default CreateWebhook

